/* eslint-disable react/prop-types */
// A "Ducks"-style collection
// https://github.com/erikras/ducks-modular-redux
import { push } from 'react-router-redux';
import { getMessage } from 'util/locale';
import { isEqual, get, omit, reject, uniqBy } from 'lodash';

import { trackError } from 'util/error';

// TODO - break up this massive file before the Department of Justice files an antitrust lawsuit.
/*  Thoughts on breaking this up...
    - Use the sub-Duck style demonstrated in current/uiwidgets - action constants and creators
      associated with sub-ducks can move out.
    - Action functions at this Duck level that are organized around certain concepts can be
      moved out of this file into action files like zoneDrawingActions.js. Some possible concepts:
      - drawing
      - exporting
      - script creation
      - product changes (maybe worth creating a sub-Duck for)
    - Can be done incrementally.
*/

// Util
import { treatErrorAs, logError, handleErrorsInQueue } from 'api/errors';
import { log } from 'util/logging';
import {
  addCreateStartMessage,
  addMergeStartMessage,
  addSplitStartMessage
} from './drawingInstructionMessages.js';
import { getDrawingStateForCreateShape } from 'util/zoneDrawing';
import { isPolygonKinked } from 'geometry/geojson/kinks';
import { getGeometry } from 'geometry/geojson/convert';
import { area } from '3rd-party-wrappers/turf';
import { ignorePollingAbort } from 'api/shared';
import isCustomScript from 'util/isCustom';

// Constants
import { exportScopeTypes } from 'constants/exportSelector';
import { errorLevel, errorDisplayTypes } from 'constants/errors';
import {
  drawingStates,
  exportTypes,
  inputStates,
  messageIds,
  messageTypes,
  NITROGEN_APPLICATION_RATE_TYPES
} from 'constants/values';
import {
  applicationProperties,
  applicationTypes,
  editZoneTypes,
  editZoneReturnTypes,
  scriptTypes,
  managementZonesTypes
} from 'constants/scripts';
import { TRIVIAL_AREA_THRESHOLD } from 'geometry/integrity/constants';

// finders
import { findActivitiesForFieldUuid, findActivity } from 'activities/finders';
import {
  findNewManualScript,
  findScriptProducts,
  findManualZoneGeneratorScript
} from 'scripts/finders';
import { findAdvancedScriptTemplate } from 'advanced/finders';
import { fetchStartZoneGeneration } from 'advanced/loaders';

// selectors
import { getActivityByActivityType } from 'activities/selectors';
import { getHarvestHistoryParam } from 'advanced/selectors';
import { getBoundariesByFieldUuid } from 'boundaries/selectors';
import { getExportParams, getRawScriptList } from 'export-selector/selectors';
import { getSoilCompositionForZones } from 'imagery/soil/selectors';
import { getScriptBasedManagementZones, getManualScriptParamsForTemplateCall } from 'manual/selectors';
import {
  getFormatForProduct,
  getFertilizerRateByNitrogenRateAndProduct,
  getNitrogenRateByFertilizerRateAndProduct
} from 'products/fertility-selectors';
import { setActiveScriptId, setActiveZoneId, setFatalError, setScriptIsFetching } from 'current';
import {
  getActiveFieldId,
  getActiveFieldUuid,
  getActiveProductYear,
  getActiveScript,
  getActiveScriptId,
  getActiveUserId,
  getActiveZoneId,
  getDrawingState,
  getDuplicateModal,
  getLastShapeDrawn,
  getRenameModal,
  getSelectedZoneIds,
  getActiveMeasurementDistance,
  getNitrogenApplicationRateType,
  getActiveFieldCountryCode
} from 'current/selectors';
import { getActiveCropId } from 'current/currentSelectors';
import {
  closeDeleteModal,
  closeDuplicateModal,
  closeRenameModal,
  closeSetPrimaryModal,
  duplicateTypes,
  setExportOptionsIsOpen,
  setScriptOptionsIsOpen,
  openDuplicateModal,
  closePreferredDeviceModal
} from 'current/uiwidgets/';
import {
  getPlantingPlanParams,
  getPlantingPlanParamsForTemplateCall
} from 'planting-plan-form/selectors';
import { getPlantingPlanParamsUiValidationResult } from 'validations/selectors';
import { getActiveScenarioPlantingPlanParams } from 'advanced/scenarios/selectors';
import { getPreferredDeviceId } from 'devices/selectors';
import { getFieldIdsForOperation } from 'fields/selectors';
import {
  getFertilityProductSubstanceSetForScript,
  getFertilityScriptTypeBySubstanceTypeUuid
} from 'scripts/fertility-selectors';
import {
  getActivityTypeForScript,
  getApplicationTypeForScriptType,
  getCropForScriptId,
  getDisplayPrimaryScriptByActivityType,
  getProductsForScript,
  getScriptById,
  getScriptTypeForScript,
  hasDisplayPrimaryScriptByActivityType,
  isDisplayPrimaryScript,
  isEditableScript,
  isSavedScript,
  scriptTypeIsNitrogen
} from 'scripts/selectors';
import {
  getLastZoneId,
  getApplicationRateUnitByType
} from 'scripts/zones/selectors';
import { getProductApplicationProductsInZones } from 'scripts/zones/productApplication-selectors';
import { zoneHasSeedApplicationOfProductId } from 'scripts/zones/planting-selectors';
import {
  get2ndToLastSnapshot,
  getFirstSnapshot,
  getSnapshotById,
  getNewSnapshot
} from 'scripts/drawing-history/selectors';
import {
  getProductValidationValues,
  getSupportInfo,
  getUnitsOfMeasureTemplate,
} from 'user/selectors';

// validations
import {
  validateScriptName,
  validateZoneApplicationProperty
} from 'current/validators';
import { setValidationResult } from 'validations';

// Actions
import {
  addToast,
  addSuccessToast,
  addErrorToast,
  addTransitionModal,
  clearMessages,
  removeTransitionModal
} from 'messages';
import { lockSaveTrigger, unlockSaveTrigger } from 'current/uiwidgets/locks';
import { clearSelectedZones } from 'current/uiwidgets/selected-zones';
import {
  clearLastShapeDrawn,
  setDrawingState
} from 'current/uiwidgets/drawing';

import { storeRawScriptList } from 'export-selector';
import { setActiveScenarioUuid } from 'advanced/scenarios';
import zoneReducer from './zones';
import { default as historyReducer, snapshotIds } from './drawing-history';
import { setScriptBasedManagementZones } from 'manual';
import { formatProductApplicationProperty } from './productApplicationPropertyUtil';
import {
  fetchDuplicateManualScriptTemplate,
  fetchScriptByIdTankMix,
  fetchStatusDownloadScript
} from 'scripts/loaders';
import {
  sendBeginBulkDownloadScripts,
  sendBeginSingleDownloadScript,
  sendDeleteScriptMutation,
  sendSaveScriptTankMix,
  sendUpdatePrescriptionAsPrimary,
  sendUpdateScriptMetadata
} from 'scripts/senders';
import {
  navigateToUrl,
  getEditRouteForScriptId,
  getEditRouteFromScript,
  getManageRoute
} from 'util/routes';
import { getZonesWithArea } from 'util/zoneOperations';
import { parseScript } from 'scripts/scriptLoadParser';
import { parseScriptForSave } from 'scripts/scriptSaveParser';
import { getProductRateUnitTypeByFormat } from '../util/unit';
import { inferUnit } from '../util/unitConversion';
import {
  getProductById,
  getFormatFromApplicationIncorporationMethod,
  getFormatFromZones
} from '../products/selectors';
import { getIncorporationMethodForScript } from 'scripts/application-selectors';
import { setResourceLoaded, setResourcePending } from 'loading/index.js';
import { resourceType } from 'constants/loading';
import { goBack } from 'script-flow/index.js';
import { AGRONOMIC_SEASONS_BY_COUNTRY, MULTIPLE_AGRONOMIC_SEASONS_COUNTRIES } from 'constants/advanced';

import { CorsWorker as Worker } from 'util/corsWorker';

// Action Types
export const typePrefix = 'vra/scripts/';
export const actions = [
  'ADD_PIVOT_ZONE',
  'ADD_POLYGON_ZONE',
  'ADD_SQUARE_ZONE',
  'ZONE_HISTORY_INIT',
  'ZONE_HISTORY_POP',
  'ZONE_HISTORY_PUSH',
  'ZONE_HISTORY_REVERT_TO_SAVED',
  'ZONE_HISTORY_REVERT_TO_SNAPSHOT',
  'FETCH_NEW_SCRIPT_BEGIN',
  'FETCH_NEW_SCRIPT_FAIL',
  'MERGE_ZONES',
  'REMOVE_PRODUCT_APPLICATION',
  'REMOVE_SCRIPTS',
  'SET_SCRIPT_NAME',
  'SET_SCRIPT_NITRAPYRIN',
  'SET_SCRIPT_NAME_TO_SAVE',
  'SET_SCRIPT_STATE',
  'SET_SCRIPT_SOIL_COMPOSITION',
  'SET_GEOMETRY_VIOLATIONS',
  'SPLIT_ZONE',
  'STORE_ADVANCED_SCRIPT',
  'STORE_ALL_SCRIPTS',
  'STORE_SCRIPT',
  'UPDATE_PRODUCT_APPLICATION_PROPERTY',
  'UPDATE_FERTILITY_SCRIPT_TYPE',

  // analytics
  'CREATE_SHAPE',
  'CREATE_SHAPE_DONE',
  'MERGE_ZONES_DONE',
  'SPLIT_ZONE_DONE',
  'RENAME_SCRIPT',
  'CACHE_HANK_RESPONSE'
].reduce((result, key) => {
  result[key] = `${typePrefix}${key}`;
  return result;
}, {});

// Reducer
export default function reducer(state = {}, action = {}) { // NOSONAR False-Positive, Reducer Exception
  switch (action.type) {
    case actions.FETCH_NEW_SCRIPT_BEGIN:
      return {
        ...state,
        isFetching: true
      };

    case actions.FETCH_NEW_SCRIPT_FAIL:
    case actions.FETCH_NEW_SCRIPT_COMPLETE:
      return {
        ...state,
        isFetching: false
      };

    case actions.REMOVE_SCRIPTS:
      // omit array of discarded script uuids
      return omit(state, action.payload);

    case actions.SET_SCRIPT_NAME_TO_SAVE:
      return {
        ...state,
        [action.payload.scriptId]: {
          ...state[action.payload.scriptId],
          meta: {
            ...state[action.payload.scriptId].meta,
            name: action.payload.scriptName,
            scriptState: inputStates.CHANGED
          }
        }
      };

    case actions.SET_SCRIPT_NITRAPYRIN:
      const script = state[action.payload.scriptId];
      let zones = {};
      for (let key of Object.keys(script.zones)) {
        zones[key] = {
          ...script.zones[key],
          meta: {
            ...script.zones[key].meta,
            applications: {
              ...script.zones[key].meta.applications,
              stabilizer: [
                action.payload.hasNitrapyrin ? action.payload.nitrapyrin : null
              ]
            }
          }
        };
      }
      return {
        ...state,
        [action.payload.scriptId]: {
          ...script,
          summary: {
            ...script.summary,
            fertility: [
              {
                ...script.summary.fertility[0],
                stabilizer: action.payload.hasNitrapyrin
                  ? action.payload.nitrapyrin
                  : null
              }
            ]
          },
          zones
        }
      };

    case actions.SET_SCRIPT_NAME:
      return {
        ...state,
        [action.payload.scriptId]: {
          ...state[action.payload.scriptId],
          name: action.payload.scriptName,
          meta: {
            ...state[action.payload.scriptId].meta,
            name: action.payload.scriptName
          }
        }
      };

    case actions.SET_GEOMETRY_VIOLATIONS:
      return {
        ...state,
        [action.payload.scriptId]: {
          ...state[action.payload.scriptId],
          meta: {
            ...state[action.payload.scriptId].meta,
            geometryViolations: action.payload.violations
          }
        }
      };

    case actions.STORE_ALL_SCRIPTS:
      return {
        ...state,
        ...action.payload
      };

    case actions.STORE_SCRIPT:
      return {
        ...state,
        [action.payload.id]: action.payload
      };

    // Actions that pass through to zone reducer.
    case actions.ADD_PIVOT_ZONE:
    case actions.ADD_POLYGON_ZONE:
    case actions.ADD_SQUARE_ZONE:
    case actions.MERGE_ZONES:
    case actions.REMOVE_PRODUCT_APPLICATION:
    case actions.SPLIT_ZONE:
    case actions.UPDATE_PRODUCT_APPLICATION_PROPERTY:
    case actions.SET_SCRIPT_SOIL_COMPOSITION:
      return {
        ...state,
        [action.payload.scriptId]: {
          ...state[action.payload.scriptId],
          zones: zoneReducer(state[action.payload.scriptId].zones, action)
        }
      };
    case 'CREATE_PRODUCT':
      return {
        ...state,
        [action.payload.scriptId]: {
          ...state[action.payload.scriptId],
          zones: zoneReducer(state[action.payload.scriptId].zones, action)
        }
      };

    // Actions that pass through to drawing history reducer and also change current zones.
    case actions.ZONE_HISTORY_POP:
    case actions.ZONE_HISTORY_REVERT_TO_SNAPSHOT:
    case actions.ZONE_HISTORY_REVERT_TO_SAVED:
      return {
        ...state,
        [action.payload.scriptId]: {
          ...state[action.payload.scriptId],
          zones: action.payload.zones,
          zoneHistory: historyReducer(
            state[action.payload.scriptId].zoneHistory,
            action
          )
        }
      };

    // Actions that pass through to drawing history reducer without changing current zones (only changing history).
    case actions.ZONE_HISTORY_PUSH:
    case actions.ZONE_HISTORY_INIT:
      return {
        ...state,
        [action.payload.scriptId]: {
          ...state[action.payload.scriptId],
          zoneHistory: historyReducer(
            state[action.payload.scriptId].zoneHistory,
            action
          )
        }
      };

    case actions.SET_SCRIPT_STATE:
      return {
        ...state,
        [action.payload.scriptId]: {
          ...state[action.payload.scriptId],
          meta: {
            ...state[action.payload.scriptId].meta,
            scriptState: action.payload.scriptState
          }
        }
      };

    case actions.UPDATE_FERTILITY_SCRIPT_TYPE:
      return {
        ...state,
        [action.payload.scriptId]: {
          ...state[action.payload.scriptId],
          tag: action.payload.fertilityScriptTypeTag
        }
      };

    case actions.SET_TODAYS_PRODUCT_YEARS:
      return {
        ...state,
        todaysProductYears: action.payload.todaysProductYears
      };

    case actions.CACHE_HANK_RESPONSE:
      return {
        ...state,
        cachedHankResponse: action.payload
      };

    default:
      return state;
  }
}

// Action Creators
export const setScriptState = ({ scriptId, scriptState }) => {
  return {
    type: actions.SET_SCRIPT_STATE,
    payload: { scriptId, scriptState }
  };
};

export const snapshotDrawing = (scriptId, snapshotId) => {
  return (dispatch, getState) => {
    const script = getScriptById(getState(), scriptId);
    const snapshot = getNewSnapshot(script.zones, snapshotId);
    dispatch({
      type: actions.ZONE_HISTORY_PUSH,
      payload: { scriptId, zones: snapshot.zones, snapshotId }
    });
  };
};

export const snapshotSoilComposition = (scriptId) => {
  return (dispatch, getState) => {
    const state = getState();
    const { zones } = getScriptById(state, scriptId);
    const fieldId = getActiveFieldId(state);
    const soilComposition = getSoilCompositionForZones({
      fieldId,
      state,
      zones
    });
    dispatch({
      type: actions.SET_SCRIPT_SOIL_COMPOSITION,
      payload: { scriptId, soilComposition }
    });
  };
};

export const snapshotSoilCompositionForActiveScriptId = () => {
  return (dispatch, getState) => {
    const state = getState();
    const scriptId = getActiveScriptId(state);
    if (scriptId) {
      dispatch(snapshotSoilComposition(scriptId));
    }
  };
};

export const initZoneHistory = (scriptId) => {
  return (dispatch, getState) => {
    const script = getScriptById(getState(), scriptId);
    const snapshot = getNewSnapshot(script.zones);
    dispatch({
      type: actions.ZONE_HISTORY_INIT,
      payload: { scriptId, zones: snapshot.zones }
    });
  };
};

export const undoZoneHistory = (scriptId) => {
  return (dispatch, getState) => {
    const script = getScriptById(getState(), scriptId);
    // The last snapshot should represent current state with convention to take a snapshot after each drawing change.
    // So undoing is reverting to the 2nd-to-last snapshot.
    const snapshot = script && get2ndToLastSnapshot(script.zoneHistory);
    if (snapshot) {
      dispatch({
        type: actions.ZONE_HISTORY_POP,
        payload: { zones: snapshot.zones, scriptId }
      });
    }
  };
};

export const revertZoneHistoryToSnapshot = (scriptId, snapshotId) => {
  return (dispatch, getState) => {
    const script = getScriptById(getState(), scriptId);
    const snapshot = script && getSnapshotById(script.zoneHistory, snapshotId);
    if (snapshot) {
      dispatch({
        type: actions.ZONE_HISTORY_REVERT_TO_SNAPSHOT,
        payload: { zones: snapshot.zones, scriptId, snapshotId }
      });
    }
  };
};

export const revertZoneHistoryToSaved = (scriptId) => {
  return (dispatch, getState) => {
    const script = getScriptById(getState(), scriptId);
    const snapshot = script && getFirstSnapshot(script.zoneHistory);
    const zones = snapshot ? snapshot.zones : script.zones; // if snapshot doesn't exist, use current zones
    dispatch({
      type: actions.ZONE_HISTORY_REVERT_TO_SAVED,
      payload: { zones, scriptId }
    });
  };
};

// intended as different than deleting scripts. only remove these
// from scripts store, but don't delete on backend.
export const removeScripts = (scriptUuids) => {
  return {
    type: actions.REMOVE_SCRIPTS,
    payload: scriptUuids
  };
};

export const deleteScriptViaScriptManagement = ({ scriptId }) => {
  return (dispatch, getState) => {
    const state = getState();
    const script = getScriptById(state, scriptId);
    const activityType = getActivityTypeForScript(script);
    const displayPrimaryScriptId = get(
      getDisplayPrimaryScriptByActivityType(state, activityType),
      'id'
    );

    return Promise.resolve()
    .then(() => {
      return sendDeleteScriptMutation({ scriptId });
    })
    .then(() => {
      const rawListMinusDeleted = reject(getRawScriptList(state), [
        'id',
        scriptId
      ]);
      dispatch(storeRawScriptList(rawListMinusDeleted));
      dispatch(removeScripts([scriptId]));
    })
    .then(() => {
      // set active script id to primary since we just deleted the active script
      // if there is no primary script, the active script is set to null
      dispatch(setActiveScriptId(displayPrimaryScriptId || null));
      dispatch(
        addSuccessToast(getMessage('text__success--delete-prescription'))
      );
    })
    .catch(
      treatErrorAs({
        severity: errorLevel.WARN,
        messageKey: 'help-text__failure--save',
        andHandleNow: true
      })
    )
    .then(() => {
      dispatch(closeDeleteModal());
    });
  };
};

export const storeScript = (script) => {
  if (!script) {
    logError('No script available to store.');
    return null;
  }
  return {
    type: actions.STORE_SCRIPT,
    payload: script
  };
};

export const setDisplayPrimaryScript = ({ scriptId }) => {
  return (dispatch, getState) => {
    const state = getState();
    const fieldId = getActiveFieldId(state);
    const fieldUuid = getActiveFieldUuid(state);
    const userId = getActiveUserId(state);
    const newDisplayPrimaryScript = getScriptById(state, scriptId);
    const activityType = getActivityTypeForScript(newDisplayPrimaryScript);
    let rawScriptList = getRawScriptList(state);

    // capture current display primary script
    const oldDisplayPrimaryScript = getDisplayPrimaryScriptByActivityType(
      state,
      activityType
    );

    const oldDisplayPrimaryScriptActivityUuid = oldDisplayPrimaryScript?.['activity-uuid'];
    const newDisplayPrimaryScriptActivityUuid = newDisplayPrimaryScript?.['activity-uuid'];
    const scriptsAreLinked = oldDisplayPrimaryScriptActivityUuid === newDisplayPrimaryScriptActivityUuid;

    return Promise.resolve()
    .then(() => {
      if (oldDisplayPrimaryScriptActivityUuid && !scriptsAreLinked) {
        sendUpdateScriptMetadata({
          fieldId,
          updatedMetadata: { primary: false },
          scriptId: oldDisplayPrimaryScript.id,
          userId
        });
      }
    })
    .then(() => sendUpdatePrescriptionAsPrimary({ fieldUuid, scriptId }))
    .then(() => {
      const newPrimaryScriptId = scriptId;
      const oldPrimaryScriptId = get(oldDisplayPrimaryScript, 'id');

      // TODO: Migrate this to finders/processors - should make this file smaller as a bonus
      fetchScriptByIdTankMix({ userId, fieldId, scriptId: newPrimaryScriptId })
      .then((script) => {
        rawScriptList.push(script);
        rawScriptList = uniqBy(rawScriptList, 'id');
        return findScriptProducts({ dispatch, getState, script }).then(
          (scriptProducts) =>
            parseScript({ fieldId, getState, script, scriptProducts })
        );
      })
      .then((parsedScript) => {
        dispatch(storeScript(parsedScript));
        dispatch(setActiveScriptId(newPrimaryScriptId));
        dispatch(addSuccessToast(getMessage('text__success--set-primary')));
        dispatch(snapshotSoilComposition(scriptId));
      })
      .then(() => {
        // set this manually briefly so that we can pick up the correct primaries without having to wait for the
        // old primary fetch
        if (oldPrimaryScriptId) {
          oldDisplayPrimaryScript.primary = false;
        }
        // run this to pick up new primaries for manual flow
        findActivitiesForFieldUuid({ dispatch, getState, fieldUuid })
        .then(() => getScriptBasedManagementZones(getState()))
        .then((scriptBasedManagementZones) =>
          dispatch(
            setScriptBasedManagementZones(scriptBasedManagementZones)
          )
        );
      })
      .then(() => {
        if (oldPrimaryScriptId) {
          fetchScriptByIdTankMix({ userId, fieldId, scriptId: oldPrimaryScriptId })
          .then((script) => {
            rawScriptList.push(script);
            rawScriptList = uniqBy(rawScriptList, 'id');
            return findScriptProducts({
              dispatch,
              getState,
              script
            }).then((scriptProducts) =>
              parseScript({ fieldId, getState, script, scriptProducts })
            );
          })
          .then((parsedScript) => {
            dispatch(storeScript(parsedScript));
          });
        }
      });
    })
    .then(() => {
      dispatch(storeRawScriptList(rawScriptList));
    })
    .then(() => {
      dispatch(closeSetPrimaryModal());
    });
  };
};

export const saveScript = ({ scriptId, cropPlan, redirectUrl, silent = false }) => {
  return (dispatch, getState) => {
    const state = getState();

    if (redirectUrl) {
      dispatch(
        addTransitionModal({
          id: messageIds.SAVING_PRESCRIPTION,
          bodyHtmlMessageKey: 'text__in-progress--create-prescription'
        }));
    }
    const fieldId = getActiveFieldId(state);
    const fieldUuid = getActiveFieldUuid(state);
    let script = getScriptById(state, scriptId);
    const userId = getActiveUserId(state);

    if (isSavedScript(script)) {
      return Promise.resolve(scriptId);
    }

    let rawScriptList = getRawScriptList(state);
    script = parseScriptForSave({
      getState,
      script: Object.assign({}, script)
    });

    return new Promise((resolve) => {
      // TODO: Add error handling, pull out into separate function
      Promise.resolve()
      .then(() => dispatch(lockSaveTrigger()))
      .then(() => sendSaveScriptTankMix({ fieldId, script, userId }))
      .then(() => {
        if (!silent) {
          if (cropPlan?.saveAsPrimary && getScriptTypeForScript(script) === cropPlan?.scriptType) {
            dispatch(setDisplayPrimaryScript({ scriptId }));
          }
          dispatch(addSuccessToast(getMessage('text__success--save')));
        }
        return fetchScriptByIdTankMix({ userId, fieldId, scriptId });
      })
      .then((script) => {
        rawScriptList.push(script);
        rawScriptList = uniqBy(rawScriptList, 'id');
        return findScriptProducts({ dispatch, getState, script }).then(
          (scriptProducts) => {
            return parseScript({ fieldId, getState, script, scriptProducts });
          }
        );
      })
      .then((parsedScript) => {
        dispatch(storeScript(parsedScript));
        dispatch(snapshotSoilComposition(scriptId));

        const state = getState();
        const activityType = getActivityTypeForScript(parsedScript);

        if (isDisplayPrimaryScript(state, parsedScript)) {
          // run this to pick up any zone updates for manual flow
          findActivitiesForFieldUuid({ dispatch, getState, fieldUuid })
          .then(() => getScriptBasedManagementZones(getState()))
          .then((scriptBasedManagementZones) =>
            dispatch(
              setScriptBasedManagementZones(scriptBasedManagementZones)
            )
          );
        }

        if (!hasDisplayPrimaryScriptByActivityType(state, activityType)) {
          dispatch(setDisplayPrimaryScript({ scriptId }));
        }
        resolve(scriptId);
      })
      .then(() => {
        dispatch(storeRawScriptList(rawScriptList));
      })
      .catch((err) => {
        if (!silent) {
          treatErrorAs({
            severity: errorLevel.WARN,
            messageKey: 'help-text__failure--save',
            andHandleNow: true
          })(err);
        }
        resolve(scriptId);
      })
      .then(() => dispatch(unlockSaveTrigger()))
      .then(() => handleErrorsInQueue()) // Just in case
      .catch(
        treatErrorAs({
          message: `untrapped error`,
          severity: errorLevel.INFO
        })
      )
      .finally(() => {
        if (redirectUrl) {
          dispatch(removeTransitionModal({ id: messageIds.SAVING_PRESCRIPTION }));
          setTimeout(() => navigateToUrl(redirectUrl), 1000);
        }
      });
    });
  };
};

export const appendDownloadIframe = (jobUuid) => {
  return (dispatch, getState) => {
    const state = getState();
    const iframeId = 'downloadiframe';

    let iframe = document.createElement('iframe');
    iframe.src = `/api/mycroft/v2/prescriptions/download/${jobUuid}`;
    iframe.height = '0px';
    iframe.id = iframeId;
    iframe.onload = () => {
      // TODO - this handler is not being called, though download completes. Seems to be a common issue for Chrome.
      const downloadIframe = document.getElementById('downloadiframe');
      // check for errors and dispatch error if the content doc title is empty
      if (
        downloadIframe.contentDocument
        && !!downloadIframe.contentDocument.title
      ) {
        document.body.removeChild(document.getElementById('downloadiframe'));
        dispatch(
          addErrorToast(
            getMessage('help-text__failure--download', getSupportInfo(state))
          )
        );
      }
      // otherwise, we don't dispatch a success message. the download is
      // its own reward.
    };
    document.body.appendChild(iframe);
  };
};

export const downloadScript = ({ scriptId }) => {
  return (dispatch, getState) => {
    const state = getState();
    const fieldId = getActiveFieldId(state);
    const preferredDeviceId = getPreferredDeviceId(state);
    const userId = getActiveUserId(state);
    const modalId = messageIds.DOWNLOAD_FILE;
    const unitTemplate = getUnitsOfMeasureTemplate(state);
    const rowSpacing = parseInt(getActiveMeasurementDistance(state), 10);

    // close options menu
    dispatch(setScriptOptionsIsOpen({ scriptId, status: false }));
    return Promise.resolve()
    .then(() => dispatch(saveScript({ scriptId }))) // save script first
    .then(() => {
      // then proceed to download if successful

      dispatch(
        addTransitionModal({
          id: modalId,
          bodyHtmlMessageKey: 'text__in-progress--download-job'
        })
      );
      // request download jobuuid
      dispatch(closePreferredDeviceModal());
      return sendBeginSingleDownloadScript({ fieldId, preferredDeviceId, scriptId, unitTemplate, userId, rowSpacing });
    })
    .then((jobUuid) => {
      // poll status
      return fetchStatusDownloadScript({
        jobUuid,
        userId,
        downloadType: exportTypes.SINGLE
      }).then(() => jobUuid);
    })
    .then((jobUuid) => {
      // append iframe for testing if successful
      dispatch(appendDownloadIframe(jobUuid));
      dispatch(removeTransitionModal({ id: modalId }));
    })
    .catch(
      treatErrorAs({
        // dispatch an error if jobuuid request fails, or job fails or times out
        severity: errorLevel.BLOCK,
        messageKey: 'help-text__failure--download',
        messageParams: getSupportInfo(state),
        andHandleNow: true
      })
    )
    .then(() => {
      dispatch(removeTransitionModal({ id: modalId }));
    });
  };
};

export const bulkDownloadScripts = () => {
  return (dispatch, getState) => {
    const state = getState();
    const preferredDeviceId = getPreferredDeviceId(state);
    const userId = getActiveUserId(state);
    const activeFieldIds = [parseInt(getActiveFieldId(state), 10)];
    const season = getActiveProductYear(state);
    const { scopeType, scriptType, primaryOnly, operationUuid } = getExportParams(state);
    const operationFieldIds = getFieldIdsForOperation(state, operationUuid);
    const modalId = messageIds.BULK_DOWNLOAD_FILES;
    const unitTemplate = getUnitsOfMeasureTemplate(state);
    const rowSpacing = parseInt(getActiveMeasurementDistance(state), 10);
    const fieldIds = scopeType === exportScopeTypes.OPERATION
      ? operationFieldIds
      : activeFieldIds;

    // close options menu
    dispatch(
      setExportOptionsIsOpen({ exportType: exportTypes.BULK, status: false })
    );
    dispatch(
      addTransitionModal({
        id: modalId,
        bodyHtmlMessageKey: 'text__in-progress--download-job--bulk'
      })
    );
    return Promise.resolve()
    .then( () => {
      dispatch(closePreferredDeviceModal());
      return sendBeginBulkDownloadScripts({
        fieldIds,
        season,
        preferredDeviceId,
        primaryOnly,
        scriptType,
        unitTemplate,
        userId,
        rowSpacing
      });
    }) // request download jobuuid
    .then( (jobUuid) => {
      return fetchStatusDownloadScript({ jobUuid, userId, downloadType: exportTypes.BULK }) // poll status
      .then( () => jobUuid );
    })
    .then((jobUuid) => {
      // append iframe for testing if successful
      dispatch(appendDownloadIframe(jobUuid));
    })
    .catch(
      treatErrorAs({
        // dispatch an error if job uuid request fails or job fails or times out
        severity: errorLevel.BLOCK,
        messageKey: 'help-text__failure--download',
        messageParams: getSupportInfo(state),
        andHandleNow: true
      })
    )
    .then(() => {
      dispatch(removeTransitionModal({ id: modalId }));
    });
  };
};

export const downloadScriptsForExportType = ({ exportType, scriptId }) => {
  if (exportType === exportTypes.SINGLE) {
    return downloadScript({ scriptId });
  }
  return bulkDownloadScripts();
};

export const duplicateScript = ({ scriptId }) => {
  return (dispatch, getState) => {
    const state = getState();
    const fieldId = getActiveFieldId(state);
    const userId = getActiveUserId(state);
    const { value: newName, duplicateType } = getDuplicateModal(state);
    const modalId = 'duplicate-modal';
    const script = getScriptById(state, scriptId);
    const activityUuid = get(
      getActivityByActivityType(state, getActivityTypeForScript(script)),
      'uuid'
    );

    return Promise.resolve()
    .then(() => {
      dispatch(closeDuplicateModal());
      dispatch(
        addTransitionModal({
          id: modalId,
          bodyHtmlMessageKey: 'text__in-progress--duplicate-prescription'
        })
      );
      return fetchDuplicateManualScriptTemplate({
        activityUuid,
        fieldId,
        scriptId,
        userId
      });
    })
    .catch(
      treatErrorAs({
        severity: errorLevel.WARN,
        messageKey: 'help-text__failure--duplicate-prescription',
        messageParams: getSupportInfo(state),
        andHandleNow: true,
        andStopHere: false // clear the way to know if an error is a save error
      })
    )
    .then((script) => {
      script.name = newName;
      return findScriptProducts({ dispatch, getState, script }).then(
        (scriptProducts) =>
          parseScript({ fieldId, getState, script, scriptProducts })
      );
    })
    .then((duplicatedScript) => {
      const duplicatedScriptId = duplicatedScript.id;

      dispatch(storeScript(duplicatedScript));
      // update script state to changed so we can save it
      dispatch(
        setScriptState({
          scriptId: duplicatedScriptId,
          scriptState: inputStates.CHANGED
        })
      );

      return dispatch(saveScript({ scriptId: duplicatedScriptId }));
    })
    .catch(
      treatErrorAs({
        severity: errorLevel.WARN,
        messageKey: 'help-text__failure--save',
        andHandleNow: true,
        andStopHere: false // don't stop being an error
      })
    )
    .then((duplicatedScriptId) => {
      dispatch(
        addSuccessToast(getMessage('text__success--duplicate-prescription'))
      );
      // if we were duplicating because we were trying to edit,
      // send to script creator.
      if (duplicateType === duplicateTypes.EDIT) {
        dispatch(setActiveScriptId(duplicatedScriptId));
        dispatch(snapshotSoilComposition(duplicatedScriptId));
        dispatch(push(getEditRouteForScriptId(state, duplicatedScriptId)));
      }
    })
    .catch(
      treatErrorAs({
        severity: errorLevel.WARN,
        messageKey: 'help-text__failure--duplicate-prescription',
        messageParams: getSupportInfo(state),
        andHandleNow: true
      })
    )
    .then(() => {
      dispatch(removeTransitionModal({ id: modalId }));
    });
  };
};

// Set the script name with a save still pending.
export const setScriptNameToSave = ({ scriptId, scriptName }) => {
  return {
    type: actions.SET_SCRIPT_NAME_TO_SAVE,
    payload: { scriptId, scriptName }
  };
};

export const setNitrapyrin = ({ scriptId, hasNitrapyrin, nitrapyrin }) => {
  return {
    type: actions.SET_SCRIPT_NITRAPYRIN,
    payload: { scriptId, hasNitrapyrin, nitrapyrin }
  };
};

// Set the script name after a save has been completed.
export const setScriptName = ({ scriptId, scriptName }) => {
  return {
    type: actions.SET_SCRIPT_NAME,
    payload: { scriptId, scriptName }
  };
};

export const updateScriptNameToSave = (scriptName) => {
  return (dispatch, getState) => {
    let state = getState();
    const scriptId = getActiveScriptId(state);
    const rawValidatorResult = validateScriptName({ scriptName });
    dispatch(setValidationResult({ key: 'rename', rawValidatorResult }));
    dispatch(setScriptNameToSave({ scriptId, scriptName }));
  };
};

export const setGeometryViolations = ({ scriptId, violations }) => ({
  type: actions.SET_GEOMETRY_VIOLATIONS,
  payload: { scriptId, violations }
});

export const editScript = ({ scriptId, uiLocation }) => {
  return (dispatch, getState) => {
    const state = getState();
    const script = getScriptById(state, scriptId);

    if (isEditableScript(state, script)) {
      dispatch(snapshotSoilComposition(scriptId));
      dispatch(push(getEditRouteForScriptId(state, scriptId)));
    } else {
      dispatch(
        openDuplicateModal({
          scriptId,
          duplicateType: duplicateTypes.EDIT,
          uiLocation
        })
      );
    }
  };
};

export const fetchNewManualScript = () => {
  return (dispatch, getState) => {
    dispatch(
      addTransitionModal({
        id: 'new-manual-script',
        bodyHtmlMessageKey: 'text__in-progress--create-prescription'
      })
    );

    const state = getState();
    const userId = getActiveUserId(state);
    const fieldId = getActiveFieldId(state);
    const templateParams = getManualScriptParamsForTemplateCall(state);
    const { managementZonesId } = templateParams;

    const promise = findActivity({
      dispatch,
      getState,
      params: state.plantingPlan.params
    });

    return promise.then((activity) => {
      templateParams.activityUuid = activity.uuid;
      const findManualScript = managementZonesId === managementZonesTypes.GENERATE_ZONES
        ? findManualZoneGeneratorScript({ fieldId, templateParams, getState })
        : findNewManualScript({ dispatch, fieldId, getState, templateParams, userId });

      return Promise.resolve(findManualScript)
      .then((script) => {
        return findScriptProducts({
          dispatch,
          getState,
          script,
          templateParams
        })
        .then((scriptProducts) => {
          return parseScript({
            fieldId,
            getState,
            script,
            scriptProducts,
            templateParams,
            scrubProperties: true
          });
        });
      })
      .catch((error) => {
        // TODO
        // Ideally, we should handle error specifically, which we can do in the future
        // Here we just append catch() to each then() as a placeholder.
        throw error;
      })
      .then((parsedScript) => {
        dispatch(storeScript(parsedScript));
        dispatch(setActiveScriptId(parsedScript.id));
        dispatch(snapshotSoilComposition(parsedScript.id));
        dispatch(push(getEditRouteFromScript(getState(), parsedScript)));
        dispatch(
          removeTransitionModal({
            id: 'new-manual-script'
          })
        );
      })
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        dispatch(
          removeTransitionModal({
            id: 'new-manual-script'
          })
        );
      });

    });
  };
};

export const renameScript = ({ scriptId }) => {
  return (dispatch, getState) => {
    const state = getState();
    const fieldId = getActiveFieldId(state);
    const userId = getActiveUserId(state);
    const { value: newName } = getRenameModal(state);

    return (
      Promise.resolve()
      // update name of script
      .then(() =>
        sendUpdateScriptMetadata({
          fieldId,
          updatedMetadata: { name: newName },
          scriptId,
          userId
        })
      )
      .then(() => {
        // Update script in store to reflect new name.
        dispatch(setScriptName({ scriptId, scriptName: newName }));

        dispatch(closeRenameModal());
        dispatch(
          addSuccessToast(getMessage('text__success--rename-prescription'))
        );
      })
      .catch(
        treatErrorAs({
          severity: errorLevel.WARN,
          messageKey: 'help-text__failure--rename',
          andHandleNow: true
        })
      )
      .then(() => {
        dispatch(closeRenameModal());
      })
    );
  };
};

export const storeScriptList = ({ scripts = {} }) => {
  return {
    type: actions.STORE_ALL_SCRIPTS,
    payload: scripts
  };
};

export const validateProductApplicationProperty = ({
  property,
  scriptId,
  scriptType,
  value,
  zoneId
}) => {
  return (dispatch, getState) => {
    const state = getState();
    const cropId = getActiveCropId(state);
    let format;
    let substanceSet;
    if (scriptType === scriptTypes.FERTILITY) {
      const script = getScriptById(state, scriptId);
      const [product] = getProductsForScript(script);
      format = getFormatForProduct(product);
      substanceSet = getFertilityProductSubstanceSetForScript(script);
    } else if (scriptType === scriptTypes.APPLICATION) {
      const script = getScriptById(state, scriptId);
      format = getFormatFromApplicationIncorporationMethod(
        getIncorporationMethodForScript(script)
      );
      substanceSet = getFertilityProductSubstanceSetForScript(script);
    }

    const rawValidatorResult = validateZoneApplicationProperty({
      cropId,
      format,
      productValidationValues: getProductValidationValues(state),
      property,
      scriptType,
      substanceSet,
      value: get(value, 'q', value)
    });

    const validationResult = {
      key: `${scriptId}-zones-${zoneId}-${property}`,
      resultedFromUserInput: true,
      rawValidatorResult
    };
    dispatch(setValidationResult(validationResult));
  };
};

// This function will validate the user's input value, then convert it if necessary and store
// the converted value in the state.
export const updateProductApplicationProperty = ({
  scriptId,
  zoneId,
  property,
  value
}) => {
  return (dispatch, getState) => {
    let state = getState();
    const script = getScriptById(state, scriptId);
    const scriptType = getScriptTypeForScript(script);
    const { zones } = script;
    const format = getFormatFromZones(zones);

    let applicationType = getApplicationTypeForScriptType(scriptType);
    // adjust application type for the liquid property
    if (
      scriptType === scriptTypes.PLANTING
      && property === applicationProperties.FERTILIZER_RATE
    ) {
      applicationType = applicationTypes.FERTILIZER;
    }

    let propertyKey;
    switch (property) {
      // SO MANY RATES
      case applicationProperties.FERTILIZER_RATE:
      case applicationProperties.SEED_RATE:
        propertyKey = applicationProperties.RATE;
        break;
      default:
        propertyKey = property;
    }

    let product;
    if (property === 'product' && scriptType !== scriptTypes.PLANTING) {
      if (scriptType === scriptTypes.APPLICATION) {
        product = getProductById({
          state,
          applicationType: applicationTypes.APPLICATION,
          id: value
        });
        dispatch({
          type: 'CREATE_PRODUCT',
          payload: {
            applicationType: applicationTypes.APPLICATION,
            product,
            rate: {
              q: null,
              u: inferUnit(
                getProductRateUnitTypeByFormat(getFormatForProduct(product))
              ).unitKey
            },
            zoneId,
            scriptId
          }
        });
      } else {
        product = getProductById({
          state,
          applicationType: applicationTypes.FERTILIZER,
          id: value
        });
        dispatch({
          type: 'CREATE_PRODUCT',
          payload: {
            applicationType: applicationTypes.FERTILIZER,
            product,
            rate: {
              q: 0,
              u: inferUnit(
                getProductRateUnitTypeByFormat(getFormatForProduct(product))
              ).unitKey
            },
            'nitrogen-rate': {
              q: 0,
              u: inferUnit('area-density.application.dry-fertilizer').unitKey
            },
            zoneId,
            scriptId
          }
        });
      }

      return;
    } else {
      product = getProductApplicationProductsInZones({ scriptType, zones })[0];
    }

    const crop = getCropForScriptId(state, scriptId);
    let convertedValue = formatProductApplicationProperty({
      applicationType, format, crop, getState, product, property, scriptType, value
    });

    const isUpdatingFertilizerRateOnNitrogenScript
      = scriptTypeIsNitrogen(script)
      && property === applicationProperties.FERTILIZER_RATE
      && getNitrogenApplicationRateType(state)
        === NITROGEN_APPLICATION_RATE_TYPES.fertilizer
      && !isCustomScript(script);

    // If editing fertilizer rate on a nitrogen script, we want to skip validation since we
    // can only validate the nitrogen rate (we'll do that afterwards on this function).
    if (!isUpdatingFertilizerRateOnNitrogenScript) {
      dispatch(
        validateProductApplicationProperty({
          property,
          scriptId,
          scriptType,
          value,
          zoneId
        })
      );
    }

    dispatch({
      type: actions.UPDATE_PRODUCT_APPLICATION_PROPERTY,
      payload: {
        applicationType,
        propertyToUpdate: { [propertyKey]: convertedValue },
        scriptId,
        zoneId
      }
    });

    // if updating nitrogen rate, update fertilizer rate accordingly
    if (property === applicationProperties.NITROGEN_RATE) {
      const updatedFertilizerRate = getFertilizerRateByNitrogenRateAndProduct(
        state,
        convertedValue,
        product
      );

      dispatch({
        type: actions.UPDATE_PRODUCT_APPLICATION_PROPERTY,
        payload: {
          applicationType,
          propertyToUpdate: {
            [applicationProperties.RATE]: updatedFertilizerRate
          },
          scriptId,
          zoneId
        }
      });
    }

    if (isUpdatingFertilizerRateOnNitrogenScript) {
      const updatedNitrogenRate = getNitrogenRateByFertilizerRateAndProduct(
        state,
        convertedValue,
        product
      );

      dispatch({
        type: actions.UPDATE_PRODUCT_APPLICATION_PROPERTY,
        payload: {
          applicationType,
          propertyToUpdate: {
            [applicationProperties.NITROGEN_RATE]: updatedNitrogenRate
          },
          scriptId,
          zoneId
        }
      });
      dispatch(
        validateProductApplicationProperty({
          property: applicationProperties.NITROGEN_RATE,
          scriptId,
          scriptType,
          value: updatedNitrogenRate,
          zoneId
        })
      );
    }
  };
};

// Updates a product application property in all zones, applying formatting and validation logic.
export const updateProductApplicationPropertyForZones = ({
  scriptId,
  property,
  value
}) => {
  return (dispatch, getState) => {
    const state = getState();
    scriptId = scriptId || getActiveScriptId(state);
    const { zones } = getScriptById(state, scriptId);
    Object.keys(zones).forEach((zoneId) => {
      dispatch(
        updateProductApplicationProperty({ scriptId, zoneId, property, value })
      );
    });
  };
};

// Sets rate to null for applications of a type in all zones, avoiding any formatting or validation logic.
export const removeProductRateForZones = ({ scriptId, applicationType }) => {
  return (dispatch, getState) => {
    const state = getState();
    const { zones } = getScriptById(state, scriptId);
    const unit = getApplicationRateUnitByType({ zones, applicationType });
    Object.keys(zones).forEach((zoneId) => {
      dispatch({
        type: actions.UPDATE_PRODUCT_APPLICATION_PROPERTY,
        payload: {
          applicationType,
          propertyToUpdate: {
            [applicationProperties.RATE]: { q: null, u: unit }
          },
          scriptId,
          zoneId
        }
      });
    });
  };
};

export const updateSeedApplicationProduct = ({
  scriptId,
  zoneId,
  productId
}) => {
  return updateProductApplicationProperty({
    scriptId,
    zoneId,
    property: 'product',
    value: productId
  });
};

export const updateFertilizerApplicationProduct = ({
  scriptId,
  zoneId,
  productId
}) => {
  return updateProductApplicationProperty({
    scriptId,
    zoneId,
    property: 'product',
    value: productId
  });
};

export const updateProtectionProductForZones = ({ scriptId, productId }) => {
  return (dispatch, getState) => {
    const state = getState();
    const { zones } = getScriptById(state, scriptId);
    Object.keys(zones).forEach((zoneId) => {
      dispatch(
        updateFertilizerApplicationProduct({ scriptId, zoneId, productId })
      );
    });
  };
};

export const updateFertilizerApplicationProductForZones = ({
  scriptId,
  productId
}) => {
  return (dispatch, getState) => {
    const state = getState();
    const { zones } = getScriptById(state, scriptId);
    Object.keys(zones).forEach((zoneId) => {
      dispatch(
        updateFertilizerApplicationProduct({ scriptId, zoneId, productId })
      );
    });
  };
};

export const updateSeedApplicationProductForZones = ({
  scriptId,
  productId,
  oldProductId
}) => {
  return (dispatch, getState) => {
    const state = getState();
    const { zones } = getScriptById(state, scriptId);
    Object.keys(zones).forEach((zoneId) => {
      const zoneIsUpdating
        = !oldProductId
        || zoneHasSeedApplicationOfProductId(zones[zoneId], oldProductId);
      if (zoneIsUpdating) {
        dispatch(updateSeedApplicationProduct({ scriptId, zoneId, productId }));
      }
    });
    dispatch(setScriptState({ scriptId, scriptState: inputStates.CHANGED }));
  };
};

export const clearSeedApplicationProductForZones = ({ scriptId }) => {
  return (dispatch, getState) => {
    const state = getState();
    const { zones } = getScriptById(state, scriptId);

    Object.keys(zones).forEach((zoneId) => {
      dispatch(
        updateSeedApplicationProduct({ productId: null, scriptId, zoneId })
      );
    });
  };
};

export const clearFertilizerApplicationsForActiveScript = () => {
  return (dispatch, getState) => {
    const state = getState();
    const { id: scriptId, zones } = getActiveScript(state);
    Object.keys(zones).forEach((zoneId) => {
      dispatch({
        type: actions.REMOVE_PRODUCT_APPLICATION,
        payload: {
          applicationType: applicationTypes.FERTILIZER,
          scriptId,
          zoneId
        }
      });
    });
  };
};

const _createEditZonesWorker =  async (params, callback) => {
  const worker = await new Worker(
    new URL('./editZones.worker.js', import.meta.url),
    { type: 'module' },
  ).getWorker();

  worker.onmessage = (e) => {
    if (e.data.type === editZoneReturnTypes.LOG) {
      log(JSON.parse(e.data.message));
    } else if (e.data.type === editZoneReturnTypes.LOG_ERROR) {
      const errorContent = JSON.parse(e.data.error);
      trackError(errorContent.error, errorContent.context);
    } else {
      const newZones = e.data.newZones && getZonesWithArea(e.data.newZones);
      callback({ ...e.data, newZones });
      worker.terminate();
    }
  };
  worker.postMessage(params);
};

export const splitZone = ({ scriptId, zoneId }) => {
  return (dispatch, getState) => {
    const state = getState();
    const { zones } = getActiveScript(state);

    const editZonesWorkerParams = {
      type: editZoneTypes.SPLIT,
      splitZoneId: zoneId,
      zones
    };
    const editZonesCallback = ({ newZones }) => {
      dispatch(setDrawingState(drawingStates.NONE));
      if (newZones) {
        dispatch({
          type: actions.SPLIT_ZONE,
          payload: { scriptId, zoneId, zones: newZones }
        });
      }
      dispatch(clearSelectedZones());
      dispatch(snapshotDrawing(scriptId));
      dispatch(snapshotSoilComposition(scriptId));
      dispatch(clearMessages());
    };
    _createEditZonesWorker(editZonesWorkerParams, editZonesCallback);
  };
};

export const splitCancel = () => {
  return (dispatch, getState) => {
    const scriptId = getActiveScriptId(getState());
    dispatch(revertZoneHistoryToSnapshot(scriptId, snapshotIds.DRAW_START));
    dispatch(clearSelectedZones());
    dispatch(setDrawingState(drawingStates.NONE));
    dispatch(clearMessages());
    dispatch(unlockSaveTrigger());
  };
};

export const splitDone = () => {
  return (dispatch, getState) => {
    const state = getState();
    const scriptId = getActiveScriptId(state);
    const zoneId = getActiveZoneId(state);

    dispatch(clearMessages());
    if (zoneId) {
      dispatch(
        addToast({
          title: getMessage('text__in-progress'),
          messageType: messageTypes.NEUTRAL,
          showDismiss: false
        })
      );
      dispatch(splitZone({ scriptId, zoneId }));
    } else {
      dispatch(revertZoneHistoryToSnapshot(scriptId, snapshotIds.DRAW_START));
      dispatch(clearSelectedZones());
      dispatch(setDrawingState(drawingStates.NONE));
    }
    dispatch(unlockSaveTrigger());
  };
};

export const splitStart = () => {
  return (dispatch, getState) => {
    const scriptId = getActiveScriptId(getState());
    dispatch(clearSelectedZones());
    dispatch(setActiveZoneId({ zoneId: null }));
    dispatch(snapshotDrawing(scriptId, snapshotIds.DRAW_START));
    dispatch(setDrawingState(drawingStates.SPLIT_ZONE_SELECTION));
    dispatch(
      addSplitStartMessage({
        onCancelClick: () => dispatch(splitCancel()),
        onDoneClick: () => dispatch(splitDone())
      })
    );
  };
};

export const mergeZones = ({ scriptId, scriptType, zoneIds }) => {
  return (dispatch, getState) => {
    const state = getState();
    const { zones } = getActiveScript(state);

    const editZonesWorkerParams = {
      type: editZoneTypes.MERGE,
      mergeZoneIds: zoneIds,
      scriptType,
      zones,
      flags: state.user.flags
    };
    const editZonesCallback = ({ newZones }) => {
      dispatch(setDrawingState(drawingStates.NONE));
      if (newZones) {
        dispatch({
          type: actions.MERGE_ZONES,
          payload: { scriptId, zoneIds, zones: newZones }
        });
      }
      const updatedScript = getActiveScript(getState());
      const zoneId = getLastZoneId(updatedScript.zones);

      dispatch(setActiveZoneId({ zoneId }));
      dispatch(snapshotDrawing(scriptId));
      dispatch(snapshotSoilComposition(scriptId));
      dispatch(clearMessages());
    };
    _createEditZonesWorker(editZonesWorkerParams, editZonesCallback);
  };
};

export const mergeCancel = () => {
  return (dispatch, getState) => {
    const scriptId = getActiveScriptId(getState());
    dispatch(revertZoneHistoryToSnapshot(scriptId, snapshotIds.DRAW_START));
    dispatch(clearSelectedZones());
    dispatch(setDrawingState(drawingStates.NONE));
    dispatch(clearMessages());
    dispatch(unlockSaveTrigger());
  };
};

export const mergeDone = () => {
  return (dispatch, getState) => {
    const state = getState();
    const scriptId = getActiveScriptId(state);
    const script = getActiveScript(state);
    const scriptType = getScriptTypeForScript(script);
    const zoneIds = getSelectedZoneIds(state);

    dispatch(clearMessages());
    dispatch(
      addToast({
        title: getMessage('text__in-progress'),
        messageType: messageTypes.NEUTRAL,
        showDismiss: false
      })
    );
    dispatch(mergeZones({ scriptId, scriptType, zoneIds }));
    dispatch(unlockSaveTrigger());
  };
};

export const mergeStart = () => {
  return (dispatch, getState) => {
    const scriptId = getActiveScriptId(getState());
    dispatch(clearSelectedZones());
    dispatch(snapshotDrawing(scriptId, snapshotIds.DRAW_START));
    dispatch(setDrawingState(drawingStates.MERGE_ZONE_SELECTION));
    dispatch(
      addMergeStartMessage({
        onCancelClick: () => dispatch(mergeCancel()),
        onDoneClick: () => dispatch(mergeDone())
      })
    );
  };
};

export const createCancel = () => {
  return (dispatch, getState) => {
    const scriptId = getActiveScriptId(getState());
    dispatch(revertZoneHistoryToSnapshot(scriptId, snapshotIds.DRAW_START));
    dispatch(setDrawingState(drawingStates.NONE));
    dispatch(clearMessages());
    dispatch(unlockSaveTrigger());
  };
};

const _addZone = ({
  scriptId,
  drawingState,
  geoJson,
  scriptType,
  isCheckStrip
}) => {
  return (dispatch, getState) => {
    const state = getState();
    const boundaries = getBoundariesByFieldUuid(
      state,
      getActiveFieldUuid(state)
    );
    const { zones } = getActiveScript(state);

    const editZonesWorkerParams = {
      type: editZoneTypes.ADD_ZONE,
      actions,
      boundaries,
      drawingState,
      isCheckStrip,
      newZoneGeoJson: geoJson,
      scriptType,
      zones,
      flags: state.user.flags
    };
    const editZonesCallback = ({ newZones, actionType }) => {
      dispatch(setDrawingState(drawingStates.NONE));
      if (newZones) {
        dispatch({
          type: actionType,
          payload: { scriptId, zones: newZones }
        });
      }
      const zoneId = getLastZoneId(newZones || zones);
      dispatch(setActiveZoneId({ zoneId }));
      dispatch(snapshotDrawing(scriptId));
      dispatch(snapshotSoilComposition(scriptId));
      dispatch(clearMessages());
    };
    _createEditZonesWorker(editZonesWorkerParams, editZonesCallback);
  };
};

const _validateShape = ({ geoJson, drawingState }) => {
  if (!geoJson) {
    return 'help-text__rejection--incomplete-polygon';
  }

  if (
    drawingState === drawingStates.DRAWING_POLYGON
    && isPolygonKinked(getGeometry(geoJson))
  ) {
    return 'help-text__rejection--kinked-polygon';
  }

  if (area(geoJson) < TRIVIAL_AREA_THRESHOLD) {
    return 'help-text__rejection--polygon-too-small';
  }

  return null;
};

export const createDone = ({ isCheckStrip, shapeType }) => {
  return (dispatch, getState) => {
    const startTime = performance.now();
    const state = getState();
    const scriptId = getActiveScriptId(state);
    const script = getActiveScript(state);
    const scriptType = getScriptTypeForScript(script);
    const geoJson = getLastShapeDrawn(state);
    const drawingState = getDrawingState(state);
    const isCheckStripsEnabled = state.launchDarkly.scriptCreatorCheckStrips;

    dispatch(clearMessages());

    const invalidMessageId = _validateShape({ geoJson, drawingState });
    if (invalidMessageId) {
      dispatch(
        addCreateStartMessage({
          shapeType,
          onCancelClick: () => dispatch(createCancel()),
          onDoneClick: () => dispatch(createDone({ isCheckStrip: false, shapeType })),
          onDoneCheckStrip: () => dispatch(createDone({ isCheckStrip: true, shapeType })),
          isCheckStripsEnabled,
        })
      );
    } else {
      dispatch(
        addToast({
          title: getMessage('text__in-progress'),
          messageType: messageTypes.NEUTRAL,
          showDismiss: false
        })
      );
      dispatch(
        _addZone({ scriptId, drawingState, geoJson, scriptType, startTime, isCheckStrip })
      );
    }
    dispatch(unlockSaveTrigger());
  };
};

export const createStart = (shapeType) => {
  return (dispatch, getState) => {
    const state = getState();
    const scriptId = getActiveScriptId(state);
    const isCheckStripsEnabled = state.launchDarkly.scriptCreatorCheckStrips;

    dispatch(clearSelectedZones());
    dispatch(clearLastShapeDrawn());
    dispatch(snapshotDrawing(scriptId, snapshotIds.DRAW_START));
    dispatch(setDrawingState(getDrawingStateForCreateShape(shapeType)));
    dispatch(
      addCreateStartMessage({
        shapeType,
        onCancelClick: () => dispatch(createCancel()),
        onDoneClick: () => dispatch(createDone({ isCheckStrip: false, shapeType })),
        onDoneCheckStrip: () => dispatch(createDone({ isCheckStrip: true, shapeType })),
        isCheckStripsEnabled,
      })
    );
  };
};

export const manageScripts = () => {
  return (dispatch, getState) => {
    const state = getState();
    dispatch(push(getManageRoute(state)));
  };
};

export const backToLandingPage = (redirectUrl) => {
  return (dispatch) => {
    if (redirectUrl) {
      navigateToUrl(redirectUrl);
    } else {
      dispatch(manageScripts());
    }
  };
};

export const updateFertilityScriptTypeForActiveScript = ({
  substanceTypeUuid
}) => {
  return (dispatch, getState) => {
    const state = getState();
    const scriptId = getActiveScriptId(state);
    const fertilityScriptTypeTag = getFertilityScriptTypeBySubstanceTypeUuid(
      state,
      substanceTypeUuid
    );

    dispatch({
      type: actions.UPDATE_FERTILITY_SCRIPT_TYPE,
      payload: { fertilityScriptTypeTag, scriptId }
    });
    dispatch(setScriptState({ scriptId, scriptState: inputStates.CHANGED }));
  };
};

// Kicks off a call without scenario details (no economics) to start zone calculations
export const startNewZoneGeneration = () => {
  return (dispatch, getState) => {
    const state = getState();
    const fieldUuid = getActiveFieldUuid(state);
    const harvestHistory = getHarvestHistoryParam(state);
    const countryCode = getActiveFieldCountryCode(state);

    // TODO - shift to a "find" where we can not request duplicates
    // find handles cancels, but this does not poll

    dispatch(setResourcePending({
      type: resourceType.ADVANCED_SCRIPT_AVAILABILITY,
      id: resourceType.ADVANCED_SCRIPT_AVAILABILITY,
      exclusive: true
    }));

    const startZoneGenerationProcesses = MULTIPLE_AGRONOMIC_SEASONS_COUNTRIES.includes(countryCode)
      ? AGRONOMIC_SEASONS_BY_COUNTRY[countryCode].map((agronomicSeason) => fetchStartZoneGeneration({
        fieldUuid,
        harvestHistory,
        ...getPlantingPlanParamsForTemplateCall(state, agronomicSeason)
      }))
      : [fetchStartZoneGeneration({
        fieldUuid,
        harvestHistory,
        ...getPlantingPlanParamsForTemplateCall(state)
      })];

    Promise.all(startZoneGenerationProcesses)
    .then(() => {
      dispatch(setResourceLoaded({
        type: resourceType.ADVANCED_SCRIPT_AVAILABILITY,
        id: resourceType.ADVANCED_SCRIPT_AVAILABILITY
      }));
    })
    .catch(() => {
      dispatch(setResourceLoaded({
        type: resourceType.ADVANCED_SCRIPT_AVAILABILITY,
        id: resourceType.ADVANCED_SCRIPT_AVAILABILITY
      }));
      dispatch(setFatalError({ message: getMessage('help-text__starting-zone-generation-failed') }));
      dispatch(goBack());
    });
  };
};

/** Generates a new script with values from the planting params.
 *  if a planting rate is passed we'll avoid a new request to Hank and generate
 *  a new script based on the density curves, this feature is created to support the
 *  slider fetaure on step 3
 * @param { number } [newCurrentAveragePopulation]
 * @returns {AnyAction}
 * */
export const loadNewAdvancedScript = (newCurrentAveragePopulation) => {
  return (dispatch, getState) => {
    let state = getState();
    const fieldUuid = getActiveFieldUuid(state);
    const {
      activityUuid,
      cropId,
      defaultScriptName,
      goalRatio,
      grainPrice,
      numZones,
      productId,
      seasonCode,
      seedCost,
      targetYield,
      include,
      countryCode,
      agronomicSeason,
      currentAveragePopulation
    } = getPlantingPlanParamsForTemplateCall(state);
    if (!activityUuid) {
      return null;
    } // Activity has not finished being retrieved at time this function is called.
    const harvestHistory = getHarvestHistoryParam(state);

    // unset current active scenario
    dispatch(setScriptIsFetching(true));
    dispatch(setActiveScenarioUuid());
    return findAdvancedScriptTemplate({
      dispatch,
      getState,
      cropId,
      activityUuid,
      defaultScriptName,
      include,
      fieldUuid,
      goalRatio,
      grainPrice,
      harvestHistory,
      numZones,
      productId,
      seasonCode,
      seedCost,
      targetYield,
      countryCode,
      agronomicSeason,
      currentAveragePopulation,
      newCurrentAveragePopulation
    })
    .catch(ignorePollingAbort())
    .catch(
      treatErrorAs({
        severity: errorLevel.WARN,
        andHandleNow: true,
        displayErrorAs: errorDisplayTypes.MODAL,
        messageKey: 'help-text__failure--create-prescription',
        messageParams: getSupportInfo(state)
      })
    )
    .then(() => dispatch(setScriptIsFetching(false)));
  };
};

/** Generates a new script, but only if the planting params are valid and different than the current active scenario. */
export const loadNewAdvancedScriptIfChangesScenario = (dispatch, getState) => {
  const state = getState();
  if (!getPlantingPlanParamsUiValidationResult(state)?.isInvalid) {
    const activeParams = getActiveScenarioPlantingPlanParams(state);
    const newParams = getPlantingPlanParams(state);
    if (!isEqual(activeParams, newParams)) {
      dispatch(loadNewAdvancedScript());
    }
  }
};

export const cacheHankResponse = (response) => {
  return (dispatch) => {
    dispatch({
      type: actions.CACHE_HANK_RESPONSE,
      payload: response
    });
  };

};
